const LEAD_IN_BEATS = 2;
// Fraction of a sustain window that must be covered by detected sound for the
// sustain to count. Start/end timing is irrelevant — only total coverage.
const SUSTAIN_COVERAGE = 0.6;
export class RhythmRail {
    constructor(cfg, startMs) {
        Object.defineProperty(this, "cfg", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: cfg
        });
        Object.defineProperty(this, "targets", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "beatMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "nextIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "appended", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "scheduleCursor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        const beatMs = 60000 / cfg.tempoBpm;
        this.beatMs = beatMs;
        this.scheduleCursor = startMs;
        if (cfg.plannedNotes && cfg.plannedNotes.length > 0) {
            this.appendPlanned(startMs);
        }
        else {
            this.appendSequence(0, startMs);
        }
    }
    extend() {
        if (!this.cfg.infinite)
            return;
        this.appendSequence(this.targets.length, null);
        this.appended += 1;
    }
    tryLand(clusterId, nowMs) {
        if (this.cfg.timed) {
            const t = this.activeTimedFor(clusterId, nowMs);
            if (t) {
                t.hit = true;
                if (t.sustainMs > 0) {
                    t.sustainStartMs = nowMs;
                    t.heldMs = 0;
                    t.lastFeedMs = nowMs;
                    t.sustainSatisfied = false;
                }
                else {
                    t.sustainSatisfied = true;
                }
                return { target: t, offsetMs: t.time - nowMs };
            }
            return null;
        }
        const t = this.activeUntimed();
        if (t && t.clusterId === clusterId) {
            t.hit = true;
            this.nextIndex += 1;
            return { target: t, offsetMs: 0 };
        }
        return null;
    }
    activeTimed(nowMs) {
        for (const t of this.targets) {
            if (!t.hit && Math.abs(t.time - nowMs) <= this.cfg.windowMs)
                return t;
        }
        return null;
    }
    activeUntimed() {
        return this.targets[this.nextIndex] ?? null;
    }
    skipUntimed() {
        if (this.cfg.timed)
            return null;
        const t = this.targets[this.nextIndex];
        if (!t)
            return null;
        t.hit = true;
        this.nextIndex += 1;
        return t;
    }
    updateSustain(clusterId, nowMs, graceMs = 220) {
        // Loosened, coverage-only sustain scoring: the start and end no longer
        // matter — we simply accumulate how much of the sustain window was
        // covered by detected sound. Gaps do NOT reset progress; they just
        // don't add coverage. A sustain is satisfied once accumulated coverage
        // reaches a fraction of the required duration.
        void graceMs;
        for (const t of this.targets) {
            if (t.clusterId !== clusterId ||
                t.sustainMs <= 0 ||
                t.sustainSatisfied ||
                t.sustainStartMs === undefined) {
                continue;
            }
            const lastFeed = t.lastFeedMs ?? t.sustainStartMs;
            const since = nowMs - lastFeed;
            const frameHopMs = this.cfg.frameHopMs ?? 40;
            // Credit the time since the last feed, capped so a long stall
            // between feeds cannot over-credit a single detection.
            const credited = Math.min(Math.max(0, since), frameHopMs);
            t.heldMs = (t.heldMs ?? 0) + credited;
            t.lastFeedMs = nowMs;
            // Coverage threshold: 60% of the required window counts as held.
            const required = t.sustainMs * SUSTAIN_COVERAGE;
            if ((t.heldMs ?? 0) >= required) {
                t.sustainSatisfied = true;
                return t;
            }
            return null;
        }
        return null;
    }
    collectMisses(nowMs) {
        if (!this.cfg.timed)
            return [];
        const missed = [];
        for (const t of this.targets) {
            if (!t.hit && t.time + this.cfg.windowMs < nowMs) {
                t.hit = true;
                t.missed = true;
                missed.push(t);
            }
        }
        return missed;
    }
    complete() {
        if (this.cfg.infinite)
            return false;
        return this.targets.every((t) => t.hit);
    }
    appendSequence(baseOrder, startMs) {
        const beatMs = 60000 / this.cfg.tempoBpm;
        const spacing = Math.max(0.25, this.cfg.spawnIntervalBeats ?? 1);
        const breakBeats = Math.max(0, Math.min(3, this.cfg.breakBeats ?? 1));
        const breakMs = breakBeats * beatMs;
        const gapMs = Math.max(beatMs * 0.5, 120);
        this.cfg.sequence.forEach((clusterId, i) => {
            const sustainMs = this.cfg.rollSustainMs?.(clusterId, beatMs) ??
                this.cfg.sustainMsFor?.(clusterId) ??
                0;
            // The next note must not begin until the previous note's ENTIRE
            // sustain window (already folded into scheduleCursor) has elapsed,
            // plus a mandatory gap/break. This prevents a long sustained note
            // from visually/temporally overlapping the following note.
            const earliest = this.scheduleCursor + Math.max(gapMs, breakMs);
            let time;
            if (startMs !== null && i === 0) {
                // The very first note must land no earlier than the end of the
                // lead-in. For sustained notes the HIT time is the START of the
                // sustain, so we also require enough room that the sustain does
                // not begin before the lead-in has elapsed.
                const leadInEnd = startMs + LEAD_IN_BEATS * beatMs;
                time = Math.max(leadInEnd, earliest);
            }
            else {
                // Honor the spacing grid, but never earlier than `earliest`
                // (which already clears the previous sustain + gap).
                time = Math.max(earliest, this.scheduleCursor + spacing * beatMs);
            }
            // DIAGNOSTICS: verify this note does not overlap the previously
            // scheduled note's sustain window, and log the scheduling decision.
            const prev = this.targets[this.targets.length - 1];
            const noteEnd = time + sustainMs;
            if (prev) {
                const prevEnd = prev.time + prev.sustainMs;
                const overlap = prevEnd - time;
                if (overlap > 1e-6) {
                    console.warn(`[RhythmRail] ⚠ OVERLAP detected: note ` +
                        `#${baseOrder + i} '${clusterId}' start=${time.toFixed(0)}ms ` +
                        `begins ${overlap.toFixed(0)}ms BEFORE prev note ` +
                        `'${prev.clusterId}' end=${prevEnd.toFixed(0)}ms ` +
                        `(prev start=${prev.time.toFixed(0)}ms ` +
                        `sustain=${prev.sustainMs.toFixed(0)}ms). ` +
                        `cursor=${this.scheduleCursor.toFixed(0)} ` +
                        `earliest=${earliest.toFixed(0)} gap=${gapMs.toFixed(0)} ` +
                        `break=${breakMs.toFixed(0)}`);
                }
            }
            if (startMs !== null && i === 0) {
                const leadInEnd = startMs + LEAD_IN_BEATS * beatMs;
                if (time < leadInEnd - 1e-6) {
                    console.warn(`[RhythmRail] ⚠ first note starts ${(leadInEnd - time).toFixed(0)}ms ` +
                        `before lead-in end (${leadInEnd.toFixed(0)}ms)`);
                }
            }
            console.debug(`[RhythmRail] scheduled #${baseOrder + i} '${clusterId}' ` +
                `start=${time.toFixed(0)}ms sustain=${sustainMs.toFixed(0)}ms ` +
                `end=${noteEnd.toFixed(0)}ms ` +
                `(cursor=${this.scheduleCursor.toFixed(0)} → ${noteEnd.toFixed(0)}, ` +
                `spacing=${(spacing * beatMs).toFixed(0)}ms ` +
                `gap=${gapMs.toFixed(0)}ms break=${breakMs.toFixed(0)}ms)`);
            this.targets.push({
                targetHz: this.cfg.pitches?.[i] ?? 0,
                clusterId,
                order: baseOrder + i,
                time,
                hit: false,
                sustainMs,
            });
            // Advance the cursor past this note's sustain so the NEXT note is
            // scheduled strictly after it ends.
            this.scheduleCursor = time + sustainMs;
        });
        console.info(`[RhythmRail] appendSequence complete: ${this.cfg.sequence.length} ` +
            `note(s) scheduled, cursor now ${this.scheduleCursor.toFixed(0)}ms ` +
            `(base order ${baseOrder}, ${startMs !== null ? "initial" : "extend"})`);
    }
    appendPlanned(startMs) {
        const beatMs = 60000 / this.cfg.tempoBpm;
        const notes = [...(this.cfg.plannedNotes ?? [])].sort((a, b) => a.beat - b.beat);
        const leadBeats = LEAD_IN_BEATS;
        notes.forEach((note, i) => {
            const time = startMs + (leadBeats + note.beat) * beatMs;
            const sustainMs = Math.max(0, note.sustainBeats) * beatMs;
            const prev = this.targets[this.targets.length - 1];
            if (prev) {
                const prevEnd = prev.time + prev.sustainMs;
                const overlap = prevEnd - time;
                if (overlap > 1e-6) {
                    console.warn(`[RhythmRail] ⚠ planned OVERLAP: note #${i} ` +
                        `'${note.clusterId}' start=${time.toFixed(0)}ms overlaps ` +
                        `prev '${prev.clusterId}' end=${prevEnd.toFixed(0)}ms ` +
                        `by ${overlap.toFixed(0)}ms`);
                }
            }
            this.targets.push({
                targetHz: note.pitchHz ?? 0,
                clusterId: note.clusterId,
                order: i,
                time,
                hit: false,
                sustainMs,
            });
            this.scheduleCursor = Math.max(this.scheduleCursor, time + sustainMs);
        });
    }
    activeTimedFor(clusterId, nowMs) {
        let best = null;
        let bestDelta = Infinity;
        for (const t of this.targets) {
            if (t.hit || t.clusterId !== clusterId)
                continue;
            const delta = Math.abs(t.time - nowMs);
            if (delta <= this.cfg.windowMs && delta < bestDelta) {
                bestDelta = delta;
                best = t;
            }
        }
        return best;
    }
}
